/**
 * Профиль пользователя: второй канал связи и видимые сессии.
 *
 * Экрана профиля в каркасе кабинета не было: он пришёл вместе с двумя
 * требованиями, адресованными этому блоку запечатанной спецификацией
 * предыдущего.
 *
 * Проверок формата канала здесь нет ни одной. Форма тела — та же, что у входа:
 * ключ канала ровно один, и какой именно, выбирает клиент; годность значения
 * проверяет сервер. Своя проверка разошлась бы с серверной молча.
 *
 * Перерисовка после подтверждения идёт **по ответу**, а не по введённому:
 * подтверждение возвращает обновлённый профиль, и показывать набранное значило
 * бы показывать то, чего сервер, возможно, не принял.
 *
 * Признака «текущая сессия», вычисленного клиентом, нет ни одного: он приходит
 * полем ответа. «Первая в списке» и «самая свежая» — догадки, которых сервер не
 * обещал, и на первом же ответе с другим порядком они расходятся с истиной.
 *
 * Завершение текущей сессии уводит на вход: токен отзывается немедленно, и
 * вкладка, оставшаяся с отвергнутым токеном, повторяет отказ на каждом
 * действии, выглядя работающей.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient, AuthCodeInput } from '../api/client';
import type { CurrentUserData, Session } from '../api/contract';
import type { Strings } from '../i18n';
import {
  createFieldMessageSlot,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface ProfileScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  /** Токен отозван сервером: вкладке остаётся вернуться на вход. */
  readonly onSignedOut: () => void;
}

export interface ProfileScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  /** Сессии в том порядке, в котором их вернул сервер. */
  readonly sessions: readonly Session[];
  load(): Promise<void>;
  /** Первая стадия второго канала: тело уходит одним ключом канала. */
  requestChannelCode(input: AuthCodeInput): Promise<void>;
  /** Вторая стадия: тот же канал плюс код. */
  confirmChannel(code: string): Promise<void>;
  terminateSession(sessionId: string): Promise<void>;
}

/** Имя поля кода — то же, что имя поля запроса: по нему сервер называет место. */
const CODE_FIELD = 'code';

export function createProfileScreen(options: ProfileScreenOptions): ProfileScreen {
  const { client, strings, onSignedOut } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const contacts = document.createElement('p');
  const form = document.createElement('form');
  const channelCaption = document.createElement('label');
  const channelCaptionText = document.createElement('span');
  const channelField = document.createElement('input');
  const channelMessage = createFieldMessageSlot();
  const codeCaption = document.createElement('label');
  const codeCaptionText = document.createElement('span');
  const codeField = document.createElement('input');
  const codeMessage = createFieldMessageSlot();
  const submit = document.createElement('button');
  const sessionsHeading = document.createElement('h2');
  const sessionsList = document.createElement('ul');

  element.className = 'screen screen-profile';
  form.className = 'form';
  channelCaption.className = 'form-field';
  channelCaptionText.className = 'form-field__caption';
  codeCaption.className = 'form-field';
  codeCaptionText.className = 'form-field__caption';
  submit.className = 'action';

  heading.textContent = strings.profile_title;
  channelCaptionText.textContent = strings.profile_channel_add;
  codeCaptionText.textContent = strings.profile_channel_confirm;
  codeField.name = CODE_FIELD;
  submit.type = 'submit';

  channelCaption.append(channelCaptionText, channelField, channelMessage);
  codeCaption.append(codeCaptionText, codeField, codeMessage);
  sessionsHeading.textContent = strings.profile_sessions_title;

  let state: ScreenState | null = 'loading';
  let profile: CurrentUserData | null = null;
  let sessions: readonly Session[] = [];
  /** Канал, которым запрошен код, и значение, которым его запросили. */
  let asked: AuthCodeInput | null = null;

  /**
   * Канал, которого у человека нет, — тот, который и добавляется.
   *
   * Это не правило: какой канал главный и что делать со вторым, решает сервер.
   * Здесь выбирается лишь **имя поля тела**, и выбирается оно по тому, что
   * пришло в профиле.
   */
  function missingChannel(): 'phone' | 'email' | null {
    if (profile === null) {
      return null;
    }

    if (profile.email === null) {
      return 'email';
    }

    return profile.phone === null ? 'phone' : null;
  }

  function contactLine(): string {
    if (profile === null) {
      return '';
    }

    return `${profile.phone ?? ''} ${profile.email ?? ''}`.trim();
  }

  function sessionRow(session: Session): HTMLElement {
    const row = document.createElement('li');
    const line = document.createElement('span');
    const stop = document.createElement('button');

    row.className = 'session-row';
    // Устройство, оба времени и признак текущей — как их отдал сервер. Время
    // последнего использования, равное null, показывается подписью: пустое
    // место читается как «поля нет», и это ложь.
    line.textContent = [
      session.device_name,
      session.created_at,
      session.last_used_at ?? strings.profile_session_never_used,
      session.current ? strings.profile_session_current : '',
    ].join(' ');

    stop.type = 'button';
    stop.className = 'action';
    stop.textContent = strings.profile_session_terminate;
    stop.addEventListener('click', () => {
      void terminateSession(session.id);
    });

    row.append(line, stop);

    return row;
  }

  function renderSessions(): void {
    if (sessions.length === 0) {
      sessionsList.replaceChildren(renderScreenState('empty', strings));

      return;
    }

    const rows: HTMLElement[] = [];

    for (const session of sessions) {
      rows.push(sessionRow(session));
    }

    sessionsList.replaceChildren(...rows);
  }

  function render(): void {
    if (state !== null) {
      element.replaceChildren(renderScreenState(state, strings));

      return;
    }

    const channel = missingChannel();

    contacts.textContent = contactLine();

    if (channel === null) {
      // Добавлять нечего: оба канала заполнены. Форма без поля обещала бы
      // действие, которого нет.
      form.replaceChildren();
    } else {
      channelField.name = channel;
      channelField.id = channel;

      form.replaceChildren(
        channelCaption,
        ...(asked === null ? [] : [codeCaption]),
        submit,
      );
    }

    submit.textContent =
      asked === null ? strings.profile_channel_code_request : strings.profile_channel_confirm;

    renderSessions();
    element.replaceChildren(heading, contacts, form, sessionsHeading, sessionsList);
  }

  /**
   * Исход неудачного обращения — одним местом на весь экран.
   *
   * Сервер, назвавший поля, получает свои сообщения у этих полей, и содержимое
   * экрана остаётся: человеку есть что исправлять. Сервер, не назвавший ни
   * одного поля, оставляет экрану только состояние отказа — показывать рядом с
   * ним содержимое значило бы делать вид, что ничего не случилось.
   */
  function failureOf(error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;

    if (failure !== null && failure.details.length > 0) {
      render();
      renderFieldMessages(form, failure.details);

      return;
    }

    state = screenStateFor({ kind: 'failed', code: failure === null ? null : failure.code });
    render();
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    render();

    try {
      profile = await client.getCurrentUser();
      sessions = await client.listSessions();
      state = null;
    } catch (error) {
      profile = null;
      sessions = [];
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function requestChannelCode(input: AuthCodeInput): Promise<void> {
    renderFieldMessages(form, []);

    try {
      await client.requestChannelCode(input);

      asked = input;
      state = null;
      render();
    } catch (error) {
      asked = null;
      failureOf(error);
    }
  }

  async function confirmChannel(code: string): Promise<void> {
    if (asked === null) {
      return;
    }

    renderFieldMessages(form, []);

    const input = 'phone' in asked ? { phone: asked.phone, code } : { email: asked.email, code };

    try {
      // Источник истины — ответ: профиль перерисовывается пришедшим, а не
      // набранным. Введённое значение сервер вправе привести к своей форме.
      profile = await client.confirmChannel(input);
      asked = null;
      channelField.value = '';
      codeField.value = '';
      state = null;
      render();
    } catch (error) {
      failureOf(error);
    }
  }

  /** Та ли это сессия, которой выполнен текущий запрос, — по полю ответа. */
  function isCurrent(sessionId: string): boolean {
    for (const session of sessions) {
      if (session.id === sessionId) {
        return session.current;
      }
    }

    return false;
  }

  async function terminateSession(sessionId: string): Promise<void> {
    const wasCurrent = isCurrent(sessionId);

    try {
      await client.terminateSession(sessionId);
    } catch (error) {
      failureOf(error);

      return;
    }

    if (wasCurrent) {
      // Токен отозван немедленно — так записано в контракте. Перечитывать
      // список нечем: следующее обращение получило бы отказ.
      onSignedOut();

      return;
    }

    try {
      sessions = await client.listSessions();
    } catch (error) {
      failureOf(error);

      return;
    }

    render();
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const channel = missingChannel();

    if (channel === null) {
      return;
    }

    if (asked !== null) {
      void confirmChannel(codeField.value);

      return;
    }

    const value = channelField.value;

    void requestChannelCode(channel === 'phone' ? { phone: value } : { email: value });
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get sessions(): readonly Session[] {
      return sessions;
    },

    load,
    requestChannelCode,
    confirmChannel,
    terminateSession,
  };
}
